/**
 * Cliente de API para el módulo de Reportes y Analítica de Negocio.
 */

import { apiRequest } from "./api";

export interface SalesSummaryReport {
  total_sales_count: number;
  gross_sales_cents: number;
  discount_cents: number;
  tax_cents: number;
  net_sales_cents: number;
  cost_cents: number;
  gross_profit_cents: number;
  margin_percentage: number;
  average_ticket_cents: number;
}

export interface SalesPeriodItem {
  period: string;
  sales_count: number;
  total_cents: number;
  cost_cents: number;
  gross_profit_cents: number;
}

export interface SalesByPeriodReport {
  group_by: string;
  items: SalesPeriodItem[];
}

export interface TopProductItem {
  variant_id: string;
  sku: string;
  name: string;
  quantity_sold: string;
  revenue_cents: number;
  cost_cents: number;
  gross_profit_cents: number;
  margin_percentage: number;
}

export interface TopProductsReport {
  items: TopProductItem[];
}

export interface CashierPerformanceItem {
  user_id: string | null;
  user_name: string;
  sales_count: number;
  total_cents: number;
  average_ticket_cents: number;
}

export interface CashierPerformanceReport {
  items: CashierPerformanceItem[];
}

export interface InventoryValuationItem {
  warehouse_id: string;
  warehouse_name: string;
  variant_id: string;
  sku: string;
  product_name: string;
  quantity: string;
  avg_cost_cents: number;
  total_valuation_cents: number;
}

export interface InventoryValuationReport {
  total_items_count: number;
  total_valuation_cents: number;
  items: InventoryValuationItem[];
}

const BASE = "/api/v1/reports";

function buildDateParams(params?: {
  branch_id?: string;
  start_date?: string;
  end_date?: string;
}): string {
  const q = new URLSearchParams();
  if (params?.branch_id) q.set("branch_id", params.branch_id);
  if (params?.start_date) q.set("start_date", params.start_date);
  if (params?.end_date) q.set("end_date", params.end_date);
  const qs = q.toString();
  return qs ? `?${qs}` : "";
}

export function getSalesSummary(params?: {
  branch_id?: string;
  start_date?: string;
  end_date?: string;
}): Promise<SalesSummaryReport> {
  return apiRequest(`${BASE}/summary${buildDateParams(params)}`);
}

export function getSalesByPeriod(params?: {
  branch_id?: string;
  start_date?: string;
  end_date?: string;
  group_by?: "day" | "month";
}): Promise<SalesByPeriodReport> {
  const query = new URLSearchParams();
  if (params?.branch_id) query.set("branch_id", params.branch_id);
  if (params?.start_date) query.set("start_date", params.start_date);
  if (params?.end_date) query.set("end_date", params.end_date);
  if (params?.group_by) query.set("group_by", params.group_by);
  const qs = query.toString();
  return apiRequest(`${BASE}/sales-by-period${qs ? `?${qs}` : ""}`);
}

export function getTopProducts(params?: {
  branch_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
}): Promise<TopProductsReport> {
  const query = new URLSearchParams();
  if (params?.branch_id) query.set("branch_id", params.branch_id);
  if (params?.start_date) query.set("start_date", params.start_date);
  if (params?.end_date) query.set("end_date", params.end_date);
  if (params?.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiRequest(`${BASE}/top-products${qs ? `?${qs}` : ""}`);
}

export function getCashierPerformance(params?: {
  branch_id?: string;
  start_date?: string;
  end_date?: string;
}): Promise<CashierPerformanceReport> {
  return apiRequest(`${BASE}/cashiers${buildDateParams(params)}`);
}

export function getInventoryValuation(params?: {
  warehouse_id?: string;
}): Promise<InventoryValuationReport> {
  const q = params?.warehouse_id ? `?warehouse_id=${params.warehouse_id}` : "";
  return apiRequest(`${BASE}/inventory-valuation${q}`);
}

export async function downloadSalesCsv(params?: {
  branch_id?: string;
  start_date?: string;
  end_date?: string;
}): Promise<void> {
  const token = localStorage.getItem("megasyr_access_token");
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(`${BASE}/export/sales.csv${buildDateParams(params)}`, {
    headers,
  });
  if (!res.ok) throw new Error("Error al descargar el archivo CSV");
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ventas_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

